// The Convex wiring for the calendar-link settings surface (bead hf-7o6).
//
// It lives HERE rather than in features/calendarFeed because the feature module is a
// pure component tree over view models — the same rule features/plan follows, and the
// reason its whole surface can be tested without a backend. `ConvexSetupProvider.tsx`
// is the precedent for a Convex-aware component in this directory.
//
// It lives here rather than in App.tsx because App.tsx is a heavily shared file, and a
// subscription plus three mutations plus a date formatter is not a change other agents
// should have to read around. App.tsx gains one case.

import { CALENDAR_FEED_MESSAGES } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo } from "react";

import {
  CalendarFeedSettings,
  useCalendarFeed,
  type FeedTitleMode,
} from "../features/calendarFeed";
import { useConnectivity } from "../offline/offline";
import {
  FEED_PATH_PREFIX,
  feedOriginFrom,
  mintFeedTokenMutation,
  myFeedQuery,
  revokeFeedTokenMutation,
  setFeedTitleModeMutation,
} from "./calendarFeed";

export interface CalendarFeedRouteProps {
  /**
   * The circle whose feed this is.
   *
   * `calendarFeeds.*` take a raw `circleId`, not a `publicPlanRef` — only
   * `domain/plan.read` uses the ref selector. The plan read model already carries it,
   * so it is passed down rather than looked up a second time.
   */
  readonly circleId: string;
  /**
   * The Convex deployment URL, so the link can be built against the right origin.
   *
   * Defaults to the same environment variable `main.tsx` reads to decide whether the
   * app is live at all — so if this component is rendering, the value is there. It
   * stays a prop because a test must be able to render this without a build-time env.
   */
  readonly convexUrl?: string;
  /**
   * The HTTP-actions endpoint, when the deployment has told us what it is.
   *
   * `convex/CONTRACT.md` documents `VITE_CONVEX_SITE_URL` as exactly that. When it is
   * configured it wins; otherwise the origin is derived from `convexUrl`.
   */
  readonly siteUrl?: string;
  /** Overridable so a test can render this at a fixed moment. */
  readonly now?: () => number;
  /** Standalone by default; level two when composed into the settings page. */
  readonly headingLevel?: 1 | 2;
}

function configured(name: string): string {
  const value: unknown = import.meta.env[name];
  return typeof value === "string" ? value : "";
}

export function CalendarFeedRoute({
  circleId,
  convexUrl = configured("VITE_CONVEX_URL"),
  siteUrl = configured("VITE_CONVEX_SITE_URL"),
  now = Date.now,
  headingLevel = 1,
}: CalendarFeedRouteProps) {
  const feed = useQuery(myFeedQuery, { circleId });
  const { actionsPaused } = useConnectivity();

  const mintFeedToken = useMutation(mintFeedTokenMutation);
  const revokeFeedToken = useMutation(revokeFeedTokenMutation);
  const setFeedTitleMode = useMutation(setFeedTitleModeMutation);

  const feedOrigin = useMemo(() => feedOriginFrom(convexUrl, siteUrl), [convexUrl, siteUrl]);

  const linkFor = useCallback(
    (token: string) => `${feedOrigin}${FEED_PATH_PREFIX}${token}`,
    [feedOrigin],
  );

  const controller = useCalendarFeed({
    linkFor,
    mint: useCallback(
      (idempotencyKey: string) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a calendar-link change."));
        return mintFeedToken({ circleId, idempotencyKey });
      },
      [actionsPaused, circleId, mintFeedToken],
    ),
    revoke: useCallback(() => {
      if (actionsPaused)
        return Promise.reject(new Error("Connection is not ready for a calendar-link change."));
      return revokeFeedToken({ circleId });
    }, [actionsPaused, circleId, revokeFeedToken]),
    setTitleMode: useCallback(
      (titleMode: FeedTitleMode) => {
        if (actionsPaused)
          return Promise.reject(new Error("Connection is not ready for a calendar-link change."));
        return setFeedTitleMode({ circleId, titleMode });
      },
      [actionsPaused, circleId, setFeedTitleMode],
    ),
  });

  // `undefined` is Convex's "still loading". `null` is a real answer — this member has
  // never made a link — and the surface has an empty state for exactly that, so only
  // the first is a reason to render nothing yet.
  if (feed === undefined) return null;

  return (
    <CalendarFeedSettings
      actionsDisabled={actionsPaused}
      headingLevel={headingLevel}
      controller={controller}
      createdOnLabel={
        feed === null
          ? ""
          : CALENDAR_FEED_MESSAGES.createdOn(
              // The one date on this surface. Formatted here, in the app, because
              // product-language never formats a date (its CONTRACT.md forbids it).
              new Date(feed.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }),
            )
      }
      feed={feed}
      now={now()}
    />
  );
}
